import { reaction } from 'mobx';
import { IReactionDisposer } from 'mobx/lib/internal';
import RegionRenderService, {
    RegionRenderConfig,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/performance/base-region-render-service';
import { ESourceType, GuideComponent } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component';
import { RenderArea } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/performance/layered-render/render-area';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IRenderRectConfig } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/type';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';

/**
 * 参考线ui层
 */
export default class GuideRenderService extends RegionRenderService {
    private slideGuideComponent: GuideComponent;
    private masterGuideComponent: GuideComponent;
    private layoutGuideComponent: GuideComponent;
    private reaction: IReactionDisposer | null;
    private isShowHistoryPanel = false;
    public constructor(props) {
        super(props);
        this.renderArea = new RenderArea(
            this.width,
            this.height,
            this.scale,
            this.application,
            this.renderRectConfig,
            true,
        );
        this.buildGuideLine();
        this.reaction = reaction(() => [
            this.getAppService().editableServiceLayer?.masterLayoutEditService.editType,
        ], () => {
            requestAnimationFrame(() => {
                this.buildGuideLine();
            });
        });
    }

    public dispose(): void {
        this.reaction?.();
        this.reaction = null;
    }

    // eslint-disable-next-line complexity
    public updateWithData(config: RegionRenderConfig): void {
        const { width, height, scale, renderRectConfig } = config;
        const { slideLeft, slideTop } = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const { currentScrollX = 0, currentScrollY = 0, contentSize = {} } = renderRectConfig || {};
        const diffProps = Object.keys(contentSize)
            .filter(key => contentSize[key] !== this.renderRectConfig?.contentSize?.[key]);
        if (
            this.width === width
            && this.height === height
            && this.scale === scale
            && this.left === slideLeft
            && this.top === slideTop
            && this.currentScrollX === currentScrollX
            && this.currentScrollY === currentScrollY
            && diffProps.length === 0
            && this.isShowHistoryPanel === this.getAppService().editableServiceLayer?.historyManager.isShowHistoryPanel
        ) return;
        this.isShowHistoryPanel = this.getAppService().editableServiceLayer?.historyManager.isShowHistoryPanel ?? false;
        this.width = width;
        this.height = height;
        this.left = slideLeft;
        this.top = slideTop;
        this.scale = scale;
        this.currentScrollX = currentScrollX;
        this.currentScrollY = currentScrollY;
        this.renderRectConfig = renderRectConfig;
        this.updateInfo(width, height, scale, renderRectConfig);
    }

    public updateInfo(width: number, height: number, scale: number, renderRectConfig?: IRenderRectConfig): void {
        const roundWidth = Math.round(width);
        const roundHeight = Math.round(height);
        this.scale = scale;
        this.application.width = roundWidth;
        this.application.height = roundHeight;
        this.application.stage.transform.scaleX = scale;
        this.application.stage.transform.scaleY = scale;
        let { slideLeft, slideTop } = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        if (renderRectConfig) {
            this.renderRectConfig = renderRectConfig;
            const { contentSize, scrollLeft, scrollTop, currentScrollX = 0, currentScrollY = 0 } = renderRectConfig;
            slideLeft = contentSize.left + scrollLeft + currentScrollX;
            slideTop = contentSize.top + scrollTop + currentScrollY;
        }
        this.buildGuideLine();
        this.application.requestRender();
    }

    private buildGuideLine() {
        if (!this.slideGuideComponent) {
            this.slideGuideComponent = new GuideComponent({
                type: ESourceType.slide,
                isUiComponent: true,
            });
            this.renderArea.updateComponents(this.slideGuideComponent);
        } else {
            this.slideGuideComponent.renderGuides();
        }

        if (!this.masterGuideComponent) {
            this.masterGuideComponent = new GuideComponent({
                type: ESourceType.master,
                isUiComponent: true,
            });
            this.renderArea.updateComponents(this.masterGuideComponent);
        } else {
            this.masterGuideComponent.renderGuides();
        }

        if (!this.layoutGuideComponent) {
            this.layoutGuideComponent = new GuideComponent({
                type: ESourceType.layout,
                isUiComponent: true,
            });
            this.renderArea.updateComponents(this.layoutGuideComponent);
        } else {
            this.layoutGuideComponent.renderGuides();
        }
    }

    private getAppService = () => AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
}
